import useTranslation from '@app/hooks/use-translation';
import React, { useState } from 'react';
import { useOutletContext } from 'react-router';

import { useGetRecordsByMFTaskQuery } from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-tasks.api';

import RubberTable from '@app/components/rubber-table';

import { TableColumn, TableItem } from '@app/components/rubber-table/table.types';

import { CircleButton, Paginator, Toggle } from '@ui';

import { MonitoringTasksRecordsRequestType } from '@app/interfaces/qm-module/qm-manage/qm-monioring-tasks.type';

import { TableFilter } from '@app/components/table/table.type';

import { navigationRoutes } from '@app/utils/navigation-routes';

import { useGetUserInfoQuery } from '@app/store/api/get-user-info.api';

import { MonitoringTaskFormOutlet } from './monitoring-task-settings-layout';

const MonitoringTaskSettingsRecords = () => {
  const { data: userInfo } = useGetUserInfoQuery();
  const role = userInfo?.qm_role;
  const { t } = useTranslation('pages.qmManage.monitoring_tasks');
  const { mFTaskObject } = useOutletContext<MonitoringTaskFormOutlet>();
  const [recordsRequestData, setRecordRequestData] = useState<MonitoringTasksRecordsRequestType>({
    id: mFTaskObject?.id || '',
    limit: 10,
    offset: 0,
    filter: mFTaskObject?.status === 'DONE' && role === 'controller' ? 'complete' : 'not_complete',
  });
  // api
  const { data: MFTasksRecords } = useGetRecordsByMFTaskQuery(recordsRequestData, {
    skip: !mFTaskObject?.id,
  });
  // api

  function handleChangeTableFilter(filter: TableFilter) {
    const keys = Object.entries(filter).flat(Infinity);
    setRecordRequestData((prev) => ({
      ...prev,
      sortBy: keys[0] as 'dt_start',
      sortDesc: keys.length ? 0 < (keys[1] as number) : undefined,
    }));
  }

  const tableColumns: TableColumn[] = [
    { index: 'dt_add', title: t('table.dt_add_col') },
    { index: 'dt_start', title: t('table.dt_start_col'), filter: true },
    { index: 'dt_completion', title: t('table.dt_completion_col') },
    { index: 'operator_id', title: t('table.operator_id_col'), maxWidth: 150 },
    { index: 'team_id', title: t('table.team_id_col'), maxWidth: 150 },
    { index: 'complete', title: t('table.complete_col'), maxWidth: 110 },
    { index: 'confirmed', title: t('table.confirmed_col'), maxWidth: 110 },
    { index: 'actions', title: t('table.actions_col'), maxWidth: 100 },
  ];

  const tableRowsData: TableItem[] =
    MFTasksRecords?.mfs.map((record) => ({
      dt_add: record?.dt_add,
      dt_start: record?.dt_start,
      dt_completion: record?.dt_completion,
      team_id: record?.team?.name,
      operator_id: record?.operator?.name,
      complete: <Circle val={record.complete} />,
      confirmed: <Circle val={record.confirmed} />,
      actions: (
        <div className="w-full flex items-center">
          <CircleButton
            hintTitle={t('table.hints.monitoring_view')}
            icon="Oscilogram"
            disable={mFTaskObject?.status === 'DONE' && role === 'controller'}
            size={22}
            onClick={() =>
              window.open(
                `/${navigationRoutes.qm_manage_monitoring_tasks}/${record.mfs_id}/${record.record_id}`,
                '_blank',
              )
            }
            // link={}
          />
        </div>
      ),
    })) || [];

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[20px] text-3color py-2">
          <span className="font-bold text-[18px]">МФ: {mFTaskObject?.mform_name}</span>
          <span>
            {t('period_desc')}
            &nbsp;{t('period_desc_from')}&nbsp;
            {mFTaskObject?.dt_start ? mFTaskObject?.dt_start.split(' ')[0] : null}
            &nbsp;{t('period_desc_to')}&nbsp;
            {mFTaskObject?.dt_end.split(' ')[0]}
          </span>
        </div>
        <div className="flex items-center gap-[20px]">
          <b className="text-[18px]">
            {MFTasksRecords?.total_complete}/{MFTasksRecords?.total}
            &nbsp;&nbsp;&nbsp;
            {MFTasksRecords?.total &&
              ((MFTasksRecords?.total_complete / MFTasksRecords?.total) * 100).toFixed(1)}{' '}
            %
          </b>
          <div className="flex items-baseline self-start gap-[10px] py-2">
            <span className="text-3color">{t('show_completed')}</span>
            <Toggle
              disabled={mFTaskObject?.status === 'DONE' && role === 'controller'}
              checked={recordsRequestData.filter === 'complete'}
              onChecked={(val) => {
                val
                  ? setRecordRequestData((prev) => ({ ...prev, filter: 'all' }))
                  : setRecordRequestData((prev) => ({ ...prev, filter: 'not_complete' }));
              }}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-[20px]">
        <RubberTable
          dataSource={tableRowsData}
          columns={tableColumns}
          onFilter={handleChangeTableFilter}
        />
        <Paginator
          page={Math.ceil((recordsRequestData?.offset || 0) + 1)}
          count={MFTasksRecords?.total || 0}
          onChange={(res) => setRecordRequestData((prev) => ({ ...prev, ...res }))}
          limit={recordsRequestData.limit}
        />
      </div>
    </div>
  );
};

export default MonitoringTaskSettingsRecords;

const Circle = ({ val }) => {
  return <div className={`${val ? 'bg-basic_green' : 'bg-basic_red'} rounded-full p-2 w-fit`} />;
};
